import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import camelize from 'camelize';
import striptags from 'striptags';
import urlJoin from 'url-join';
import Slider from 'rc-slider';
import { urls } from 'app-constants';
import renderBlockErrors from './renderBlockErrors';
import Select from 'components/common/Select';
import QuillEditor from 'components/common/QuillEditor';
import MediaRelationField from 'components/views/CustomTitleBuilder/CustomTitleField/MediaRelationField';


const ImageComparison = ({
  uid,
  errors,
  image1,
  image2,
  description,
  descriptionSource,
  credit,
  creditSource,
  startPosition,
  size,
  onPreviewTextUpdate,
  onChange,
}) => {
  const [image1Data, setImage1Data] = useState(null);
  const [image2Data, setImage2Data] = useState(null);

  useEffect(() => {
    const text = [image1Data, image2Data].filter(Boolean).map(x => x.name).join(' / ');
    onPreviewTextUpdate(text);
  }, [image1Data, image2Data]);

  const handleFieldChange = (name, val) => onChange({ [name]: val });

  const sizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'full', label: 'Full width' },
  ];
  const sizeSelectVal = sizeOptions.find(opt => opt.value === size);

  const fetchData = (mediaId, onSuccess) => {
    if (!mediaId) return;
    const url = urlJoin(urls.mediaDataBase, mediaId, '?format=json');
    fetch(url, { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          console.error(response.statusText);
        }
        return response;
      })
      .then(response => response.json())
      .then(data => camelize(data))
      .then(data => onSuccess(data))
      .catch(err => console.error(err));
  };

  useEffect(() => {
    if (image1) {
      fetchData(image1, setImage1Data);
    } else {
      setImage1Data(null);
    }
  }, [image1]);

  useEffect(() => {
    if (image2) {
      fetchData(image2, setImage2Data);
    } else {
      setImage2Data(null);
    }
  }, [image2]);

  const formatDesc = val => striptags(val.replace(/<\/p>\s*<p>/gi, '</p>\n\n<p>'));

  const descriptionSourceOptions = [
    { value: 'image1_long_desc', label: 'Use image 1 long description' },
    { value: 'image1_short_desc', label: 'Use image 1 short description' },
    { value: 'image2_long_desc', label: 'Use image 2 long description' },
    { value: 'image2_short_desc', label: 'Use image 2 short description' },
    { value: 'custom', label: 'Use custom description' },
    { value: 'none', label: 'None' },
  ];
  const descriptionSourceValue = descriptionSourceOptions.find(opt => opt.value === descriptionSource);

  const creditSourceOptions = [
    { value: 'image1_credit', label: 'Use image 1 credit' },
    { value: 'image2_credit', label: 'Use image 2 credit' },
    { value: 'custom', label: 'Use custom credit' },
    { value: 'none', label: 'None' },
  ];
  const creditSourceValue = creditSourceOptions.find(opt => opt.value === creditSource);

  let derivedDescriptionValue = '';
  if (descriptionSource === 'image1_long_desc') {
    derivedDescriptionValue = image1Data ? formatDesc(image1Data.longDescription) : '';
  }
  if (descriptionSource === 'image1_short_desc') {
    derivedDescriptionValue = image1Data ? formatDesc(image1Data.shortDescription) : '';
  }
  if (descriptionSource === 'image2_long_desc') {
    derivedDescriptionValue = image2Data ? formatDesc(image2Data.longDescription) : '';
  }
  if (descriptionSource === 'image2_short_desc') {
    derivedDescriptionValue = image2Data ? formatDesc(image2Data.shortDescription) : '';
  }
  const descriptionInput = descriptionSource === 'custom' ? (
    <QuillEditor
      value={description}
      onChange={val => handleFieldChange('description', val)}
      size="small"
    />
  ) : (
    <textarea
      key={`${uid}-description-${descriptionSource}`}
      cols="30"
      rows="3"
      className="form-input"
      value={derivedDescriptionValue}
      disabled
    />
  );

  let derivedCreditValue = '';
  if (creditSource === 'image1_credit') {
    derivedCreditValue = image1Data ? striptags(image1Data.credit) : '';
  }
  if (creditSource === 'image2_credit') {
    derivedCreditValue = image2Data ? striptags(image2Data.credit) : '';
  }
  const creditInput = creditSource === 'custom' ? (
    <input
      key={`${uid}-credit-${creditSource}`}
      type="text"
      className="form-input"
      value={credit}
      onChange={e => handleFieldChange('credit', e.target.value)}
    />
  ) : (
    <input
      key={`${uid}-credit-${creditSource}`}
      type="text"
      className="form-input"
      value={derivedCreditValue}
      disabled
    />
  );

  const renderedErrors = renderBlockErrors(errors);

  return (
    <div className="content">
      <MediaRelationField
        label="Image 1"
        limitTypes={['image']}
        value={image1}
        onChange={val => handleFieldChange('image1', val)}
      />
      {renderedErrors.image1}

      <MediaRelationField
        label="Image 2"
        limitTypes={['image']}
        value={image2}
        onChange={val => handleFieldChange('image2', val)}
      />
      {renderedErrors.image2}

      <div className="mb-3">
        <div>Description:</div>
        <div className="mb-2">
          <Select
            value={descriptionSourceValue}
            options={descriptionSourceOptions}
            onChange={val => handleFieldChange('descriptionSource', val)}
          />
          {renderedErrors.descriptionSource}
        </div>
        <div>
          {descriptionInput}
          {renderedErrors.description}
        </div>
      </div>

      <div className="mb-3">
        <div>Credit:</div>
        <div className="mb-1">
          <Select
            value={creditSourceValue}
            options={creditSourceOptions}
            onChange={val => handleFieldChange('creditSource', val)}
          />
          {renderedErrors.creditSource}
        </div>
        <div>
          {creditInput}
          {renderedErrors.credit}
        </div>
      </div>

      <div className="mb-3">
        <div>Slider Start Position:</div>
        <div className="d-flex align-items-center">
          <div className="slider-field" style={{ flex: 1 }}>
            <Slider
              value={startPosition}
              onChange={val => handleFieldChange('startPosition', val)}
            />
          </div>
          <div className="ml-2">
            <div className="input-group">
              <input
                type="text"
                className="form-input input-sm"
                style={{ width: 35 }}
                value={startPosition}
                onChange={e => handleFieldChange('startPosition', e.target.value)}
              />
              <span className="input-group-addon addon-sm">%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div>Size:</div>
        <div className="mb-1">
          <Select
            value={sizeSelectVal}
            options={sizeOptions}
            onChange={val => handleFieldChange('size', val)}
          />
          {renderedErrors.size}
        </div>
      </div>
    </div>
  );
};

ImageComparison.propTypes = {
  uid: PropTypes.string,
  errors: PropTypes.object,
  image1: PropTypes.string,
  image2: PropTypes.string,
  description: PropTypes.string,
  descriptionSource: PropTypes.oneOf([
    'image1_long_desc',
    'image1_short_desc',
    'image2_long_desc',
    'image2_short_desc',
    'custom',
    'none',
  ]),
  credit: PropTypes.string,
  creditSource: PropTypes.oneOf([
    'image1_credit',
    'image2_credit',
    'custom',
    'none',
  ]),
  startPosition: PropTypes.number,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'full']),
  onPreviewTextUpdate: PropTypes.func,
  onChange: PropTypes.func,
};

export default ImageComparison;
